import { useState, useCallback } from "react";
import { commissionReportService } from "../../services";

export const useCommissionReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  /**
   * Récupère le résumé des commissions
   * @param {Object} params - Paramètres de filtrage
   */
  const getSummary = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await commissionReportService.getSummary(params);
      setSummary(result.data);
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère les détails des commissions avec pagination
   * @param {Object} params - Paramètres incluant page, limit, filtres
   */
  const getDetails = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await commissionReportService.getDetails(params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupère le rapport détaillé pour un employé spécifique
   * @param {number} employeeId - ID de l'employé
   * @param {Object} params - Paramètres de filtrage (dates)
   */
  const getEmployeeReport = useCallback(async (employeeId, params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await commissionReportService.getEmployeeReport(
        employeeId,
        params,
      );
      return result.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Exporte les données de commission
   * @param {string} format - format d'export (csv, excel)
   * @param {Object} params - Paramètres de filtrage
   */
  const exportData = useCallback(async (format = "csv", params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await commissionReportService.export(format, params);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Calcule le total des commissions pour une période
   */
  const calculateTotals = useCallback((data) => {
    if (!data || !data.byEmployee) return { total: 0, byEmployee: [] };

    return {
      total: data.byEmployee.reduce((sum, emp) => sum + emp.commission, 0),
      byEmployee: data.byEmployee.map((emp) => ({
        name: emp.employeeName,
        commission: emp.commission,
        revenue: emp.revenue,
        rate: emp.commissionRate,
      })),
    };
  }, []);

  return {
    loading,
    error,
    summary,
    getSummary,
    getDetails,
    getEmployeeReport,
    exportData,
    calculateTotals,
  };
};
